import React, { createContext, useContext, useState, useEffect } from 'react';

interface TourContextType {
  hasSeenHomeTour: boolean;
  hasSeenEditorTour: boolean;
  hasSeenLearnTour: boolean;
  hasSeenTutorialTour: boolean;
  hasSeenChallengesTour: boolean;
  hasSeenProjectsTour: boolean;
  markHomeTourComplete: () => void;
  markEditorTourComplete: () => void;
  markLearnTourComplete: () => void;
  markTutorialTourComplete: () => void;
  markChallengesTourComplete: () => void;
  markProjectsTourComplete: () => void;
  resetTours: () => void;
  startTour: (tourType: 'home' | 'editor' | 'learn' | 'tutorial' | 'challenges' , autoStart?: boolean) => void;
  stopTour: () => void;
  disableAutoStart: () => void;
  isRunningTour: boolean;
  currentTour: 'home' | 'editor' | 'learn' | 'tutorial' | 'challenges' | null;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [hasSeenHomeTour, setHasSeenHomeTour] = useState(false);
  const [hasSeenEditorTour, setHasSeenEditorTour] = useState(false);
  const [hasSeenLearnTour, setHasSeenLearnTour] = useState(false);
  const [hasSeenTutorialTour, setHasSeenTutorialTour] = useState(false);
  const [hasSeenChallengesTour, setHasSeenChallengesTour] = useState(false);
  const [hasSeenProjectsTour, setHasSeenProjectsTour] = useState(false);
  const [isRunningTour, setIsRunningTour] = useState(false);
  const [currentTour, setCurrentTour] = useState<'home' | 'editor' | 'learn' | 'tutorial' | 'challenges' | null>(null);
  const [autoStartEnabled, setAutoStartEnabled] = useState(true);

  useEffect(() => {
    const homeTour = localStorage.getItem('hasSeenHomeTour');
    const editorTour = localStorage.getItem('hasSeenEditorTour');
    const learnTour = localStorage.getItem('hasSeenLearnTour');
    const tutorialTour = localStorage.getItem('hasSeenTutorialTour');
    const challengesTour = localStorage.getItem('hasSeenChallengesTour');
    const projectsTour = localStorage.getItem('hasSeenProjectsTour');
    setHasSeenHomeTour(!!homeTour);
    setHasSeenEditorTour(!!editorTour);
    setHasSeenLearnTour(!!learnTour);
    setHasSeenTutorialTour(!!tutorialTour);
    setHasSeenChallengesTour(!!challengesTour);
    setHasSeenProjectsTour(!!projectsTour);
    setIsRunningTour(false);
    setCurrentTour(null);
  }, []);

  const markHomeTourComplete = () => {
    localStorage.setItem('hasSeenHomeTour', 'true');
    setHasSeenHomeTour(true);
    setIsRunningTour(false);
  };

  const markEditorTourComplete = () => {
    localStorage.setItem('hasSeenEditorTour', 'true');
    setHasSeenEditorTour(true);
    setIsRunningTour(false);
  };

  const markLearnTourComplete = () => {
    localStorage.setItem('hasSeenLearnTour', 'true');
    setHasSeenLearnTour(true);
    setIsRunningTour(false);
  };

  const markTutorialTourComplete = () => {
    localStorage.setItem('hasSeenTutorialTour', 'true');
    setHasSeenTutorialTour(true);
    setIsRunningTour(false);
  };

  const markChallengesTourComplete = () => {
    localStorage.setItem('hasSeenChallengesTour', 'true');
    setHasSeenChallengesTour(true);
    setIsRunningTour(false);
  };

  const markProjectsTourComplete = () => {
    localStorage.setItem('hasSeenProjectsTour', 'true');
    setHasSeenProjectsTour(true);
    setIsRunningTour(false);
  };

  const resetTours = () => {
    localStorage.removeItem('hasSeenHomeTour');
    localStorage.removeItem('hasSeenEditorTour');
    localStorage.removeItem('hasSeenLearnTour');
    localStorage.removeItem('hasSeenTutorialTour');
    localStorage.removeItem('hasSeenChallengesTour');
    localStorage.removeItem('hasSeenProjectsTour');
    setHasSeenHomeTour(false);
    setHasSeenEditorTour(false);
    setHasSeenLearnTour(false);
    setHasSeenTutorialTour(false);
    setHasSeenChallengesTour(false);
    setHasSeenProjectsTour(false);
  };

  const startTour = (tourType: 'home' | 'editor' | 'learn' | 'tutorial' | 'challenges' , autoStart: boolean = false) => {
    // Don't auto-start if auto-start is disabled or if it's not an auto-start request
    if (!autoStartEnabled && autoStart) {
      console.log('Auto-start disabled, ignoring auto-start request');
      return;
    }

    // Prevent starting a new tour if one is already running
    if (isRunningTour) {
      console.log('Tour already running, ignoring new start request');
      return;
    }
    
    // Clean up any existing tour state
    setCurrentTour(null);
    
    // Small delay to ensure clean state before starting new tour
    setTimeout(() => {
      console.log(`Starting ${tourType} tour (auto-start: ${autoStart})`);
      setIsRunningTour(true);
      setCurrentTour(tourType);
    }, 100);
  };

  const stopTour = () => {
    console.log('Stopping tour');
    setIsRunningTour(false);
    setCurrentTour(null);
  };

  const disableAutoStart = () => {
    setAutoStartEnabled(false);
  };

  // Add cleanup effect when component unmounts or route changes
  useEffect(() => {
    return () => {
      stopTour();
    };
  }, []);

  return (
    <TourContext.Provider
      value={{
        hasSeenHomeTour,
        hasSeenEditorTour,
        hasSeenLearnTour,
        hasSeenTutorialTour,
        hasSeenChallengesTour,
        hasSeenProjectsTour,
        markHomeTourComplete,
        markEditorTourComplete,
        markLearnTourComplete,
        markTutorialTourComplete,
        markChallengesTourComplete,
        markProjectsTourComplete,
        resetTours,
        startTour,
        stopTour,
        disableAutoStart,
        isRunningTour,
        currentTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
